import { Modal } from '@sequencehq/core-components'
import { useState } from 'react'
import { match } from 'ts-pattern'
import { ShortcutSelectionScreen } from './screens/ShortcutSelection'
import { CancelScreen } from './screens/cancel/Cancel'
import { RenewScreen } from './screens/Renew'
import { UpgradeScreen } from './screens/Upgrade'
import { CancelConfirmationScreen } from './screens/cancel/CancelConfirmation'
import { CancelSelection, SelectionScreen } from './scheduleShortcuts.types'

const DEFAULT_CANCEL_SELECTION: CancelSelection = {
  cancelDateType: 'immediate'
}

/**
 * Multi-screen edit-schedule flow. The modal is visible exactly while mounted:
 * the parent owns closing (via `onClose`), and screen state lives here rather
 * than in a modal context, so screens receive their navigation as props.
 */
export const EditScheduleShortcutsModal = (props: { onClose: () => void }) => {
  const [screen, setScreen] = useState<SelectionScreen>('selection')
  const [cancelSelection, setCancelSelection] = useState<CancelSelection>(
    DEFAULT_CANCEL_SELECTION
  )

  const backToSelection = () => setScreen('selection')

  return (
    <Modal isOpen onClose={props.onClose} data-testid="editScheduleShortcuts">
      <Modal.Header
        showCloseButton
        withBorder
        data-testid="editScheduleShortcuts.header"
      >
        {getScheduleShortcutTitle(screen)}
      </Modal.Header>
      {match(screen)
        .with('selection', () => (
          <ShortcutSelectionScreen onSelectScreen={setScreen} />
        ))
        .with('cancel', () => (
          <CancelScreen
            initialSelection={cancelSelection}
            onBack={backToSelection}
            onContinue={selection => {
              setCancelSelection(selection)
              setScreen('cancel-confirm')
            }}
          />
        ))
        .with('cancel-confirm', () => (
          <CancelConfirmationScreen
            cancelSelection={cancelSelection}
            onBack={() => setScreen('cancel')}
            onClose={props.onClose}
          />
        ))
        .with('renew', () => <RenewScreen onBack={backToSelection} />)
        .with('upgrade', () => <UpgradeScreen onBack={backToSelection} />)
        .exhaustive()}
    </Modal>
  )
}

const getScheduleShortcutTitle = (screen: SelectionScreen) =>
  match(screen)
    .with('selection', () => 'Edit schedule')
    .with('cancel', 'cancel-confirm', () => 'End schedule')
    .with('renew', () => 'Renew schedule')
    .with('upgrade', () => 'Upgrade schedule')
    .exhaustive()
